/**
 * CustomerProposals (QUOTE-02 + GBB) — the Quotes tab. "Build a quote" opens the native
 * Good/Better/Best equipment builder (/quotes/:id). Below it: this customer's proposals,
 * latest state per proposal (sent → viewed → accepted/declined), tier + amount.
 *
 * QUOTE-360-01 (Seth 2026-09-30: "if a tech builds a repair package, where
 * does it show in the 360? If the customer calls back in, can we see that?"):
 * this block used to read ONLY the legacy proposal-app feed — every quote a
 * tech built on site (pegasus_quote) was INVISIBLE here, which is exactly
 * where a dispatcher looks when the customer calls back. Now it merges:
 *   · native pegasus_quote rows (v_quote_list, this customer) — the tech's
 *     on-site repair options and replace proposals, the office's GBB quotes
 *   · the legacy proposal-app event feed (v_proposal_latest)
 * An accepted quote that still needs its next step wears the step ON the row
 * ("Book the return visit" / "Create the install job" — both live inside the
 * quote it opens).
 *
 * PLAN-OFFER-01 slice C (2026-10-29, mock C): a plan offer is a proposal of type
 * Plan, in the same list, with the same states. Three shapes: a plan riding a
 * quote (that quote's row says "Maintenance Plus offered · 3 yr"), a plan on its
 * own (its own row), and a plan ACCEPTED BUT UNPAID — the state that must never
 * hide — which gets its own row naming the invoice it waits on, with Record
 * Payment. Active plans leave this card for the Plan tile and the Membership tab.
 * "+ Plan" opens the Plan Builder (the office's own priced quote shows here too).
 */
import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { FileText, Send, Eye, CheckCircle2, XCircle, MessageSquare, Sparkles, Plus, ArrowRight, ChevronDown, Loader2, ShieldCheck, Receipt } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { useNavigateWithBack } from "@/lib/navBack";
import {
  useCreateQuote, displayStatus, useQuote, useAcceptOption, useBookRepairVisit,
  optionTotal, quoteExpiry, TIER_LABEL,
  type QuoteListRow, type QuoteTier, type QuoteOptionFull,
} from "@/features/quotes/quotes";
import {
  useCustomerPlanOffers, useResendPlanOffer, offerState, OFFER_STATE, offerMoney, fmtDay, offerProposalUrl, offerDaysLeft,
  type PlanOfferRow,
} from "@/features/agreements/planOffers";
import { PlanBuilderModal } from "@/features/agreements/PlanBuilder";

interface ProposalRow {
  proposal_id: string;
  latest_event: "created" | "sent" | "viewed" | "accepted" | "declined" | "message" | "draft" | "expired";
  latest_at: string;
  customer_email: string | null;
  customer_name: string | null;
  custno: string | null;
  title: string | null;
  tier_selected: string | null;
  amount: number | null;
  first_at: string | null;
  /** Native pegasus_quote rows only — drives the next-step chip. */
  native?: { quote_type: string; job_id: string | null; accepted: boolean };
  /** OFFICE-NOTE-01: the tech's note to the office (office-only, never on
   *  the customer proposal). Parsed off the quote's provenance notes. */
  office_note?: string | null;
}

export function useCustomerProposals(custno: string) {
  return useQuery({
    queryKey: ["customer_proposals", custno],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<ProposalRow[]> => {
      const [legacy, native] = await Promise.all([
        supabase
          .from("v_proposal_latest")
          .select("*")
          .eq("custno", custno)
          .order("latest_at", { ascending: false })
          .limit(100),
        supabase
          .from("v_quote_list")
          .select("*")
          .eq("custno", custno)
          .order("created_at", { ascending: false })
          .limit(100),
      ]);
      if (legacy.error) throw legacy.error;
      if (native.error) throw native.error;
      const nativeRows: ProposalRow[] = ((native.data ?? []) as QuoteListRow[]).map((q) => {
        const st = displayStatus(q);
        const ev: ProposalRow["latest_event"] =
          st === "accepted" ? "accepted"
          : st === "declined" ? "declined"
          : st === "viewed" ? "viewed"
          : st === "sent" ? "sent"
          : st === "expired" ? "expired"
          : "draft";
        return {
          proposal_id: q.id,
          latest_event: ev,
          latest_at: q.accepted_at ?? q.viewed_at ?? q.sent_at ?? q.updated_at ?? q.created_at,
          customer_email: null,
          customer_name: q.customer_name,
          custno: q.custno,
          title: q.title ?? (q.quote_type === "service" ? "Repair options" : "System proposal"),
          tier_selected: null,
          amount: q.headline_total ?? null,
          first_at: q.sent_at ?? q.created_at,
          native: { quote_type: q.quote_type, job_id: q.job_id, accepted: st === "accepted" },
          office_note: /(?:^|\n)Note to the office: ([\s\S]*)$/.exec(q.notes ?? "")?.[1]?.trim() ?? null,
        };
      });
      // Native rows win on id collision (a native quote that also logged
      // legacy events shows once, with the live status).
      const nativeIds = new Set(nativeRows.map((r) => r.proposal_id));
      const legacyRows = ((legacy.data ?? []) as ProposalRow[]).filter((r) => !nativeIds.has(r.proposal_id));
      return [...nativeRows, ...legacyRows].sort((a, b) => (b.latest_at ?? "").localeCompare(a.latest_at ?? ""));
    },
  });
}

const EVENT_STYLE: Record<
  ProposalRow["latest_event"],
  { label: string; chip: string; icon: React.ComponentType<{ className?: string }> }
> = {
  created: { label: "Drafted", chip: "bg-slate-100 text-slate-600", icon: FileText },
  draft: { label: "Draft", chip: "bg-slate-100 text-slate-600", icon: FileText },
  sent: { label: "Sent", chip: "bg-sky-50 text-sky-700", icon: Send },
  viewed: { label: "Viewed", chip: "bg-violet-50 text-violet-700", icon: Eye },
  accepted: { label: "Accepted", chip: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  declined: { label: "Declined", chip: "bg-rose-50 text-rose-700", icon: XCircle },
  expired: { label: "Expired", chip: "bg-amber-50 text-amber-800", icon: XCircle },
  message: { label: "New Message", chip: "bg-amber-50 text-amber-800", icon: MessageSquare },
};

function money(v: number | null): string | null {
  if (v === null || v === undefined) return null;
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

/* ── OFFERED-01 (Seth 2026-09-30, PROPOSAL-REWORK wave 1): expand a native
   quote right on the 360 — every option inline, chosen and not, and
   "they want this one" is ONE tap that accepts and (for a repair) books
   the return visit with the approved price + parts on the ticket. ────── */

const TIER_CHIP: Record<QuoteTier, string> = {
  best: "bg-gradient-to-b from-amber-200 to-amber-300 text-amber-900",
  better: "bg-sky-100 text-sky-800",
  good: "bg-slate-100 text-slate-600",
};

function OfferedOptions({ quoteId, quoteType, onOpen }: { quoteId: string; quoteType: string; onOpen: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuote(quoteId);
  const acceptOption = useAcceptOption();
  const bookRepair = useBookRepairVisit();
  const [busy, setBusy] = useState<string | null>(null);
  const [booked, setBooked] = useState<string | null>(null);
  const [err, setErr] = useState(false);

  if (isLoading || !data) return <div className="mx-4 mb-3 h-14 animate-pulse rounded-xl bg-slate-50" />;
  const q = data.quote;
  const exp = quoteExpiry(q);
  const service = quoteType === "service";
  const locked = q.status === "declined" || exp.expired;
  const done = () => {
    void qc.invalidateQueries({ queryKey: ["customer_proposals"] });
    void qc.invalidateQueries({ queryKey: ["quotes"] });
    void qc.invalidateQueries({ queryKey: ["quote", quoteId] });
  };

  const takeIt = async (o: QuoteOptionFull) => {
    setErr(false);
    setBusy(o.id);
    try {
      if (q.accepted_option_id !== o.id) await acceptOption.mutateAsync({ quoteId: q.id, optionId: o.id });
      if (service) {
        await bookRepair.mutateAsync({ quote: q, option: o });
        setBooked(o.id);
        done();
      } else {
        done();
        onOpen(); // install: the Create-the-job button is lit inside the quote
      }
    } catch {
      setErr(true);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-4 mb-3 overflow-hidden rounded-xl border border-slate-200 bg-slate-50/60">
      {locked && (
        <div className="flex items-center gap-2 border-b border-slate-200 bg-amber-50 px-3.5 py-2 text-xs font-semibold text-amber-800">
          {q.status === "declined" ? "Declined — open the quote to Re-bid at today's pricing." : "Expired — open the quote to Extend or Re-bid."}
          <button type="button" onClick={onOpen} className="ml-auto shrink-0 rounded-md bg-white px-2 py-1 text-[11px] font-bold text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100">Open</button>
        </div>
      )}
      {data.options.map((o) => {
        const isAccepted = q.accepted_option_id === o.id;
        const isBooked = booked === o.id;
        return (
          <div key={o.id} className={cn("flex items-center gap-3 border-b border-slate-100 px-3.5 py-2.5 last:border-b-0", isAccepted && "bg-emerald-50/60")}>
            <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase", TIER_CHIP[o.tier] ?? TIER_CHIP.good)}>{o.tier}</span>
            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-slate-800">
              {o.label || TIER_LABEL[o.tier] || "Option"}
              <span className="ml-2 font-normal text-slate-400">{o.lines.length} line{o.lines.length === 1 ? "" : "s"}</span>
            </span>
            <span className="shrink-0 text-sm font-bold tabular-nums text-slate-800">
              {optionTotal(o).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
            </span>
            {isBooked ? (
              <span className="inline-flex shrink-0 items-center gap-1 text-[11.5px] font-bold text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" /> Visit's in the queue
              </span>
            ) : isAccepted ? (
              service ? (
                <button type="button" disabled={busy !== null} onClick={() => void takeIt(o)}
                  className="shrink-0 rounded-full bg-emerald-600 px-3 py-1 text-[11.5px] font-bold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-50">
                  {busy === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Book the return visit ▸"}
                </button>
              ) : (
                <button type="button" onClick={onOpen}
                  className="shrink-0 rounded-full bg-emerald-600 px-3 py-1 text-[11.5px] font-bold text-white shadow-sm transition-colors hover:bg-emerald-700">
                  Accepted ✓ — open the job ▸
                </button>
              )
            ) : locked ? (
              <span className="shrink-0 text-[11px] font-medium text-slate-400">not chosen</span>
            ) : (
              <button type="button" disabled={busy !== null} onClick={() => void takeIt(o)}
                title={service ? "Accept this option and put the return visit in the queue — approved price + parts ride the ticket" : "Accept this option — the quote opens so you can create the install job"}
                className="shrink-0 rounded-full bg-slate-900 px-3 py-1 text-[11.5px] font-bold text-white transition-colors hover:bg-slate-700 disabled:opacity-50">
                {busy === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "They want this one ▸"}
              </button>
            )}
          </div>
        );
      })}
      {booked && (
        <div className="flex items-center gap-2 bg-emerald-50 px-3.5 py-2 text-xs font-semibold text-emerald-800">
          <CheckCircle2 className="h-3.5 w-3.5" /> Return visit created with the approved price and parts — schedule it from the Board's queue.
        </div>
      )}
      {err && (
        <div className="bg-rose-50 px-3.5 py-2 text-xs font-semibold text-rose-700">Couldn&apos;t save that — try again.</div>
      )}
    </div>
  );
}

/* ── PLAN-OFFER-01 slice C: plan offers in the proposals list ─────────────────
   The offer rows come from v_plan_offer (the same rows Memberships → Offers
   shows). Split them: an offer still riding an open quote ANNOTATES that quote's
   row; everything else — accepted · unpaid, a plan on its own, the office's
   priced quote, declined / expired plan-only — is its OWN row. Active (sold)
   plans leave the list. */
function splitPlanOffers(offers: PlanOfferRow[] | undefined, quoteIds: Set<string>) {
  const byQuote = new Map<string, PlanOfferRow>();
  const own: PlanOfferRow[] = [];
  for (const o of offers ?? []) {
    const st = offerState(o);
    if (st === "active" || st === "other") continue;
    // 360-PROPOSALS-02 (Seth 2026-10-30, R&D: a row reading "Maintenance Plus · 3 years" with no quote
    // in sight): a plan offered WITH a quote is never its own row — when that quote is not on this
    // list (a practice quote, a hidden one), its plan stays off the list with it.
    if (o.offer_quote_id && !quoteIds.has(o.offer_quote_id)) continue;
    const rides = !!o.offer_quote_id && quoteIds.has(o.offer_quote_id) && (st === "offered" || st === "viewed" || st === "expired" || st === "declined");
    if (rides) byQuote.set(o.offer_quote_id as string, o);
    else own.push(o);
  }
  return { byQuote, own };
}

const planShortName = (o: PlanOfferRow) => o.plan_name.replace(/^Sedgwick Heating\s+/i, "").replace(/\s+Plan$/i, "");

/** The kind of proposal, said once at the front of a row — the dispatcher's first question. */
function KindTag({ kind }: { kind: "repair" | "system" | "plan" }) {
  return (
    <span className={cn("mr-1.5 inline-block shrink-0 rounded px-1 py-px align-middle text-[9.5px] font-extrabold uppercase tracking-wide",
      kind === "repair" ? "bg-orange-50 text-orange-700" : kind === "system" ? "bg-sky-50 text-sky-700" : "bg-emerald-50 text-emerald-700")}>
      {kind === "repair" ? "Repair" : kind === "system" ? "System" : "Plan"}
    </span>
  );
}
/** The short reference the homeowner's page shows ("E2123774") — what a caller reads back. */
const refOf = (id: string) => (/^[0-9a-f]{8}-/i.test(id) ? `#${id.slice(0, 8).toUpperCase()}` : null);

/** 360-PROPOSALS-02 (Seth 2026-10-30: "I'm not sure if the pill buttons are filters or not"): ONE door.
 *  The path — repair, new system, or a plan on its own — is chosen here, once; the builder no longer
 *  flips between them. */
function NewProposalMenu({ custno, locno, onPlan, size = "sm" }: { custno: string; locno: string | null; onPlan: () => void; size?: "sm" | "md" }) {
  const navigate = useNavigateWithBack();
  const createQuote = useCreateQuote();
  const [open, setOpen] = useState(false);
  // The menu floats above the card (cards clip their overflow), anchored to the button.
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    }
    setOpen((v) => !v);
  };
  const create = (quote_type: "install" | "service") => {
    setOpen(false);
    createQuote.mutate(
      { custno, locno, quote_type, title: quote_type === "service" ? "Repair options" : "New System Quote" },
      { onSuccess: (id) => navigate(`/quotes/${id}`) },
    );
  };
  const items: Array<{ kind: "repair" | "system" | "plan"; title: string; desc: string; go: () => void }> = [
    { kind: "repair", title: "Repair quote", desc: "Parts and labor on the equipment that is here", go: () => create("service") },
    { kind: "system", title: "New system", desc: "Replace it — Good / Better / Best, financing, rebates", go: () => create("install") },
    { kind: "plan", title: "Maintenance plan", desc: "A plan on its own — priced, then sent or sold", go: () => { setOpen(false); onPlan(); } },
  ];
  return (
    <div className="relative">
      <button ref={btnRef} type="button" disabled={createQuote.isPending} onClick={toggle} aria-expanded={open}
        className={cn("inline-flex items-center gap-1 rounded-full bg-brand-700 font-bold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:opacity-50",
          size === "md" ? "px-3 py-1.5 text-sm" : "px-2.5 py-1 text-[11.5px]")}>
        <Plus className={size === "md" ? "h-4 w-4" : "h-3 w-3"} /> {createQuote.isPending ? "Creating…" : "New Proposal"} <ChevronDown className="h-3 w-3 opacity-80" />
      </button>
      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="fixed z-50 w-72 overflow-hidden rounded-xl border border-hairline bg-white py-1 shadow-e3" style={{ top: pos.top, right: pos.right }}>
            <div className="px-3 pb-1 pt-2 text-[10.5px] font-bold uppercase tracking-wide text-slate-400">What are we proposing?</div>
            {items.map((it) => (
              <button key={it.kind} type="button" onClick={it.go} className="flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors hover:bg-slate-50">
                <span className="mt-0.5"><KindTag kind={it.kind} /></span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-slate-800">{it.title}</span>
                  <span className="block text-[11.5px] text-slate-500">{it.desc}</span>
                </span>
              </button>
            ))}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

/** The quote row's one-line annotation: "Maintenance Plus offered · 3 yr · expires Dec 19". */
function PlanRideLine({ o }: { o: PlanOfferRow }) {
  const st = offerState(o);
  const left = offerDaysLeft(o);
  return (
    <span className={cn("mt-0.5 inline-flex items-center gap-1 text-[11.5px] font-medium", st === "declined" ? "text-slate-400" : st === "expired" ? "text-amber-700" : "text-emerald-700")}>
      <ShieldCheck className="h-3 w-3" />
      {planShortName(o)} {st === "declined" ? "declined" : st === "expired" ? "offer expired" : "offered"} · {o.term} yr
      {o.offer_mode === "included" ? " · included in the price" : ""}
      {(st === "offered" || st === "viewed") && left !== null ? ` · expires ${fmtDay(o.offer_expires_at)}` : ""}
    </span>
  );
}

/** A plan offer as its own row (accepted · unpaid, plan on its own, the office's priced quote). */
function PlanOfferLine({ o, compact, onOpenBuilder }: { o: PlanOfferRow; compact?: boolean; onOpenBuilder?: () => void }) {
  const navigate = useNavigateWithBack();
  const resend = useResendPlanOffer();
  const st = offerState(o);
  const S = OFFER_STATE[st];
  const years = o.term === 1 ? "1 year" : `${o.term} years`;
  const where = o.offered_on === "repair" ? `with Repair Quote${o.quote_dispatch_no ? ` · call #${o.quote_dispatch_no}` : ""}`
    : o.offered_on === "install" ? "with the System Proposal"
    : o.offered_on === "office" ? "priced by the office · nothing sent"
    : "the emailed bid · no system attached";
  const unpaidDetail = st === "accepted_unpaid"
    ? `Accepted ${fmtDay(o.accepted_at)}${o.accepted_via === "proposal" || o.accepted_via === "online" ? " · online" : o.accepted_via === "tech" ? " · at the door" : ""}${
        o.invoice_no || o.esc_invoice_no
          ? ` · on invoice ${o.invoice_no ?? o.esc_invoice_no}${o.invoice_balance != null ? ` · ${offerMoney(o.invoice_balance)} open` : ""}`
          : " · no invoice yet"} · activates when the invoice is paid`
    : null;
  const doResend = async () => {
    try {
      const r = await resend.mutateAsync(o.sale_id);
      const url = r.token ? offerProposalUrl({ quote_token: r.token }) : null;
      if (url) window.open(url, "_blank");
    } catch { /* error state below */ }
  };
  const open = () => {
    if (o.offer_quote_id) navigate(`/quotes/${o.offer_quote_id}`);
    else if (onOpenBuilder) onOpenBuilder();
  };
  const action = st === "accepted_unpaid" ? (
    o.offer_invoice_id
      ? <button type="button" onClick={() => navigate(`/invoices/${o.offer_invoice_id}`)} title="Open the invoice — recording its payment activates the plan"
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-600 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-amber-700"><Receipt className="h-3 w-3" /> Record Payment</button>
      : <button type="button" onClick={open} title="The invoice hasn't been built yet — it comes from the visit"
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-slate-700">Open</button>
  ) : (st === "offered" || st === "viewed" || st === "expired") && o.quote_token ? (
    <button type="button" disabled={resend.isPending} onClick={() => void doResend()} title="Same price, a new 60 days — opens the proposal page to send it"
      className="inline-flex shrink-0 items-center gap-1 rounded-full border border-hairline bg-white px-2.5 py-1 text-[11px] font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
      {resend.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Resend
    </button>
  ) : (
    <button type="button" onClick={open} title={o.offer_quote_id ? "Open the proposal" : "Open the Plan Builder"}
      className="shrink-0 rounded-md p-1 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600"><ArrowRight className="h-3.5 w-3.5" /></button>
  );

  if (compact) {
    return (
      <li>
        <div className="flex w-full items-center gap-2.5 px-4 py-2 text-left">
          <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-semibold", S.chip)}>{S.label}</span>
          <button type="button" onClick={open} className="min-w-0 flex-1 truncate text-left text-xs font-medium text-slate-700">
            <KindTag kind="plan" />{planShortName(o)} · {years}
            <span className="text-slate-400">{o.offered_on === "repair" && o.quote_dispatch_no ? ` · call #${o.quote_dispatch_no}` : o.offer_quote_id ? "" : " · on its own"}</span>
          </button>
          <span className="shrink-0 text-xs font-semibold tabular-nums text-slate-600">{offerMoney(o.total)}</span>
          {action}
        </div>
      </li>
    );
  }
  return (
    <li>
      <div className="flex w-full items-center gap-3 px-4 py-3">
        <ShieldCheck className={cn("h-4 w-4 shrink-0", st === "accepted_unpaid" ? "text-amber-600" : "text-emerald-600")} />
        <button type="button" onClick={open} className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-slate-800">{planShortName(o)} · {years}{(o.systems ?? 1) > 1 ? ` · ${o.systems} systems` : ""}</span>
            <span className="mt-0.5 block text-xs text-slate-500">Plan · {offerMoney(o.total)} · {where}</span>
            {unpaidDetail && <span className="mt-0.5 block text-xs font-medium text-amber-800">{unpaidDetail}</span>}
            {st === "declined" && o.declined_reason && <span className="mt-0.5 block text-xs text-slate-400">{o.declined_reason}</span>}
          </span>
        </button>
        <span className={cn("shrink-0 text-sm font-semibold tabular-nums", st === "accepted_unpaid" ? "text-amber-700" : "text-slate-700")}>{offerMoney(o.total)}</span>
        <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold", S.chip)}>{S.label}</span>
        <span className="shrink-0 text-[11px] text-slate-400">{fmtDay(o.accepted_at ?? o.offered_at ?? o.created_at)}</span>
        {action}
      </div>
      {resend.isError && <div className="bg-rose-50 px-4 py-1.5 text-xs font-semibold text-rose-700">Couldn&apos;t resend — try again.</div>}
    </li>
  );
}

export function CustomerProposals({ custno, locno }: { custno: string; locno?: string | null }) {
  const navigate = useNavigateWithBack();
  const { data, isLoading } = useCustomerProposals(custno);
  const { data: offers } = useCustomerPlanOffers(custno);
  const [openId, setOpenId] = useState<string | null>(null);
  const [planBuilder, setPlanBuilder] = useState(false);
  const rows = data ?? [];
  const plan = splitPlanOffers(offers, new Set(rows.map((r) => r.proposal_id)));

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-slate-900">Quotes</h2>
        <NewProposalMenu custno={custno} locno={locno ?? null} onPlan={() => setPlanBuilder(true)} size="md" />
      </div>
      {planBuilder && <PlanBuilderModal custno={custno} locno={locno ?? null} onClose={() => setPlanBuilder(false)} />}

      {isLoading ? (
        <Card className="h-32 animate-pulse bg-slate-100" />
      ) : rows.length === 0 && plan.own.length === 0 ? (
        <Card className="p-8 text-center">
          <Sparkles className="mx-auto h-6 w-6 text-slate-300" />
          <p className="mt-2 text-sm font-medium text-slate-700">No quotes yet for this customer</p>
          <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-slate-500">
            Build a Good/Better/Best system quote above. Accepted quotes flow into the invoice (repair)
            or become a job (install); anything from the legacy proposal app lands here too.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-100 bg-gradient-to-r from-sky-50/70 via-transparent to-transparent px-4 py-2.5">
            <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
              <FileText className="h-3.5 w-3.5" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">Proposals</span>
            <span className="rounded-full bg-slate-100 px-1.5 text-[11px] font-bold text-slate-600">{rows.length + plan.own.length}</span>
          </div>
          <ul className="divide-y divide-slate-100">
            {/* PLAN-OFFER-01 C: a plan accepted but unpaid never hides — it leads the list. */}
            {plan.own
              .slice()
              .sort((a, b) => (offerState(a) === "accepted_unpaid" ? -1 : 0) - (offerState(b) === "accepted_unpaid" ? -1 : 0))
              .map((o) => <PlanOfferLine key={o.sale_id} o={o} onOpenBuilder={() => setPlanBuilder(true)} />)}
            {rows.map((p) => {
              const s = EVENT_STYLE[p.latest_event];
              const Icon = s.icon;
              const expandable = !!p.native;
              const open = openId === p.proposal_id;
              // 360-AUDIT #11: rows were inert — a dispatcher could see
              // "Accepted · $12,400" and not open it. OFFERED-01: a native
              // row also EXPANDS right here — every option, one-tap book.
              return (
                <li key={p.proposal_id}>
                <div className={cn("flex w-full items-center gap-3 px-4 py-3", open && "bg-slate-50/60")}>
                  <button type="button" onClick={() => (expandable ? setOpenId(open ? null : p.proposal_id) : navigate(`/quotes/${p.proposal_id}`))}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left">
                    <Icon className={cn("h-4 w-4 shrink-0", s.chip.split(" ")[1])} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-800">
                        <KindTag kind={p.native ? (p.native.quote_type === "service" ? "repair" : "system") : "system"} />
                        {p.title ?? `Proposal ${p.proposal_id}`}
                        {refOf(p.proposal_id) && <span className="ml-1.5 font-mono text-[11px] font-normal text-slate-400">{refOf(p.proposal_id)}</span>}
                      </span>
                      <span className="mt-0.5 block text-xs text-slate-500">
                        {p.first_at ? `Sent ${format(parseISO(p.first_at), "MMM d, yyyy")}` : ""}
                        {p.latest_event === "accepted" && p.tier_selected ? ` · picked ${p.tier_selected}` : ""}
                        {p.native?.accepted && p.native.quote_type === "service" ? " · needs its return visit" : ""}
                        {p.native?.accepted && p.native.quote_type !== "service" && !p.native.job_id ? " · needs its install job" : ""}
                      </span>
                      {plan.byQuote.get(p.proposal_id) && <PlanRideLine o={plan.byQuote.get(p.proposal_id) as PlanOfferRow} />}
                      {/* OFFICE-NOTE-01: the tech's words for the office, on the row. */}
                      {p.office_note && (
                        <span className="mt-1 block rounded-md bg-amber-50/80 px-2 py-1 text-[11.5px] leading-snug text-amber-900" title="Note from the tech to the office — the customer never sees this">
                          <span className="mr-1 font-bold uppercase tracking-wide text-[9.5px] text-amber-700">Tech note</span>
                          {p.office_note}
                        </span>
                      )}
                    </span>
                  </button>
                  {money(p.amount) && (
                    <span
                      className={cn(
                        "shrink-0 text-sm font-semibold tabular-nums",
                        p.latest_event === "accepted" ? "text-emerald-700" : "text-slate-700",
                      )}
                    >
                      {money(p.amount)}
                    </span>
                  )}
                  <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold", s.chip)}>
                    {s.label}
                  </span>
                  <span className="shrink-0 text-[11px] text-slate-400">{format(parseISO(p.latest_at), "MMM d")}</span>
                  <button type="button" onClick={() => navigate(`/quotes/${p.proposal_id}`)}
                    title="Open the full quote" className="shrink-0 rounded-md p-1 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600">
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                  {expandable && (
                    <button type="button" onClick={() => setOpenId(open ? null : p.proposal_id)}
                      title={open ? "Hide the options" : "See every option offered — and book one"}
                      className="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
                      <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
                    </button>
                  )}
                </div>
                {open && p.native && (
                  <OfferedOptions
                    quoteId={p.proposal_id}
                    quoteType={p.native.quote_type}
                    onOpen={() => navigate(`/quotes/${p.proposal_id}`)}
                  />
                )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}

/** QUOTE-POLISH-01 (Seth: "finding quotes is difficult… the build & quote is
 *  hard to find — I don't want the navigation to get too long"): the Overview
 *  card. Open proposals at a glance + one-tap builders, no new nav pill — the
 *  full list stays on Service, where it's always lived ("All quotes"). */
export function ProposalsOverviewCard({ custno, locno, onViewAll }: { custno: string; locno?: string | null; onViewAll: () => void }) {
  const navigate = useNavigateWithBack();
  const { data, isLoading } = useCustomerProposals(custno);
  const { data: offers } = useCustomerPlanOffers(custno);
  const [planBuilder, setPlanBuilder] = useState(false);
  const rows = data ?? [];
  const open = rows.filter((r) => ["draft", "created", "sent", "viewed", "message"].includes(r.latest_event));
  // PLAN-OFFER-01 C: plan rows that belong on the Overview — accepted · unpaid
  // (never hides) and plans waiting on their own; declined / expired go to All quotes.
  const plan = splitPlanOffers(offers, new Set(rows.map((r) => r.proposal_id)));
  const planOpen = plan.own.filter((o) => ["accepted_unpaid", "offered", "viewed", "quoted"].includes(offerState(o)))
    .sort((a, b) => (offerState(a) === "accepted_unpaid" ? -1 : 0) - (offerState(b) === "accepted_unpaid" ? -1 : 0));
  const openCount = open.length + planOpen.length;
  const days = (iso: string | null) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : 0);
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5">
        <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
          <FileText className="h-3.5 w-3.5" />
        </span>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">Proposals</span>
        {openCount > 0 && (
          <span className="rounded-full bg-slate-100 px-1.5 text-[11px] font-bold text-slate-600">{openCount} open</span>
        )}
        <button
          type="button"
          onClick={onViewAll}
          className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-brand-600 transition-colors hover:text-brand-700"
        >
          All quotes <ArrowRight className="h-3 w-3" />
        </button>
        <NewProposalMenu custno={custno} locno={locno ?? null} onPlan={() => setPlanBuilder(true)} />
      </div>
      {planBuilder && <PlanBuilderModal custno={custno} locno={locno ?? null} onClose={() => setPlanBuilder(false)} />}
      {isLoading ? (
        <div className="m-4 h-10 animate-pulse rounded-lg bg-slate-50" />
      ) : openCount === 0 ? (
        <p className="px-4 pb-3 pt-2 text-xs text-slate-400">
          Nothing open right now — accepted and closed quotes live under &ldquo;All quotes&rdquo;.
        </p>
      ) : (
        <ul className="mt-2.5 divide-y divide-slate-100 border-t border-slate-100">
          {planOpen.slice(0, 2).map((o) => <PlanOfferLine key={o.sale_id} o={o} compact onOpenBuilder={() => setPlanBuilder(true)} />)}
          {open.slice(0, 3).map((p) => {
            const s = EVENT_STYLE[p.latest_event];
            const waiting = days(p.latest_at);
            const stale = (p.latest_event === "sent" || p.latest_event === "viewed") && waiting >= 5;
            const ride = plan.byQuote.get(p.proposal_id);
            return (
              <li key={p.proposal_id}>
                <button
                  type="button"
                  onClick={() => navigate(`/quotes/${p.proposal_id}`)}
                  className="flex w-full items-center gap-2.5 px-4 py-2 text-left transition-colors hover:bg-slate-50"
                >
                  <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-semibold", s.chip)}>{s.label}</span>
                  <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-700">
                    <KindTag kind={p.native ? (p.native.quote_type === "service" ? "repair" : "system") : "system"} />
                    {p.title ?? "Proposal"}
                    {refOf(p.proposal_id) && <span className="ml-1.5 font-mono text-[10.5px] font-normal text-slate-400">{refOf(p.proposal_id)}</span>}
                    {ride && offerState(ride) !== "declined" && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10.5px] font-semibold text-emerald-700" title={`${planShortName(ride)} offered · ${ride.term} yr`}>
                        <ShieldCheck className="h-3 w-3" /> + Plan · {ride.term} yr
                      </span>
                    )}
                  </span>
                  {money(p.amount) && (
                    <span className="shrink-0 text-xs font-semibold tabular-nums text-slate-600">{money(p.amount)}</span>
                  )}
                  <span
                    className={cn("shrink-0 text-[11px] tabular-nums", stale ? "font-bold text-amber-600" : "text-slate-400")}
                    title={stale ? "Waiting 5+ days — time for a check-in call" : "Days since the last movement"}
                  >
                    {waiting === 0 ? "today" : `${waiting}d`}
                  </span>
                </button>
              </li>
            );
          })}
          {openCount > open.slice(0, 3).length + planOpen.slice(0, 2).length && (
            <li className="px-4 py-1.5 text-[11px] text-slate-400">+ {openCount - open.slice(0, 3).length - planOpen.slice(0, 2).length} more open — see All quotes</li>
          )}
        </ul>
      )}
    </Card>
  );
}
